import { useRef, useState, useMemo, useCallback, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { Breadcrumbs, Link } from '@material-ui/core';
import { Alert, AlertIcon, Button, Text } from 'components/v4';
import { Container, useDisclosure } from '@chakra-ui/react';
import { FaListUl } from 'react-icons/fa6';

import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import TimeCellRenderer from 'components/tables/AGGrid/CustomCells/TimeCellRenderer';
import { TriggersEdit, History } from '..';
import { Select } from 'chakra-react-select';
import useColumnState from '../../../hooks/useColumnState';
import { addSpacing } from '../../../utils/utils';
import useGetTriggers from './new/queries/useGetTriggers';
import TriggerCopyModal from './new/modals/TriggerCopyModal';

import { defaultChakraSelectStyle, chakraSelectDarkMode } from '@/constants';
import walkthroughIds from './walkthroughIds';
import { LoadingIndicator } from '@/components/v4';
import ActionMenu from 'components/presentational/ActionMenu';
import { EditIcon } from '@chakra-ui/icons';
import { CopyIcon } from 'components/v4/Icon/CopyIcon';
import { AlertTitle } from '@chakra-ui/react';

const ChakraSelect = forwardRef((props, ref) => (
    <Select selectedOptionColor="brand" ref={ref} {...props} />
));
ChakraSelect.displayName = 'ChakraSelect';

const Triggers = () => {
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const gridRef = useRef();
    const chakraSelectDark = chakraSelectDarkMode(darkMode);
    const [mode, setMode] = useState('MainTable');
    const [selectedTriggerId, setSelectedTriggerId] = useState(null);

    const { addMode } = walkthroughIds;

    const {
        isOpen: isCopyOpen,
        onOpen: onCopyOpen,
        onClose: onCopyClose,
    } = useDisclosure();

    // Table data state
    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    const { isLoading, isRefetching, isFetching, refetch } =
        useGetTriggers(setTableData);

    // Mode management functions
    const switchMode = useCallback((newMode, triggerId = null) => {
        setMode(newMode);
        if (triggerId !== null) {
            setSelectedTriggerId(triggerId);
        }
    }, []);

    const resetToMainTable = useCallback(() => {
        setMode('MainTable');
        setSelectedTriggerId(null);
        onCopyClose();
        refetch();
    }, [onCopyClose, refetch]);

    // Action handlers
    const handleAdd = useCallback(() => {
        switchMode('Add');
    }, [switchMode]);

    const handleEdit = useCallback(
        (params) => {
            setSelectedTriggerId(params.data.id);
            switchMode('Edit');
        },
        [switchMode],
    );

    const handleCopy = useCallback(
        (params) => {
            setSelectedTriggerId(params.data.id);
            onCopyOpen();
        },
        [onCopyOpen],
    );

    const handleHistory = useCallback(
        (params) => {
            setSelectedTriggerId(params.data.id);
            switchMode('HistoryTable', null, params.data.id);
        },
        [switchMode],
    );

    const baseColumns = useMemo(
        () => [
            {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                minWidth: 150,
            },
            {
                field: 'description',
                headerName: 'Description',
                flex: 1.5,
                minWidth: 200,
            },
            {
                field: 'triggerType',
                headerName: 'Type',
                width: 120,
            },
            {
                field: 'entity',
                headerName: 'Entity',
                width: 120,
            },
            {
                field: 'actionName',
                headerName: 'Action',
                width: 120,
            },
            {
                field: 'eventName',
                headerName: 'Event',
                flex: 1,
                minWidth: 150,
                valueFormatter: (params) => {
                    return addSpacing(params.value);
                },
            },
            {
                field: 'historyCount',
                headerName: 'History Count',
                width: 120,
            },
            {
                field: 'lastFiredAt',
                headerName: 'Last Fired At',
                width: 180,
                cellRenderer: TimeCellRenderer,
            },
            {
                field: 'enabled',
                headerName: 'Enabled',
                width: 100,
                cellRenderer: (params) => {
                    return params.value ? 'Yes' : 'No';
                },
            },
            {
                headerName: 'Action',
                maxWidth: 100,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                lockPosition: 'right',
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: (params) => {
                    const hasHistory = params.data?.historyCount > 0;
                    const actionItems = [
                        {
                            label: 'Edit',
                            onClick: () => handleEdit(params),
                            icon: EditIcon,
                        },
                        {
                            label: 'Copy',
                            onClick: () => handleCopy(params),
                            icon: CopyIcon,
                        },
                    ];
                    if (hasHistory) {
                        actionItems.push({
                            label: 'View History',
                            onClick: () => handleHistory(params),
                            icon: FaListUl,
                        });
                    }
                    return <ActionMenu actionItems={actionItems} />;
                },
            },
        ],
        [handleEdit, handleCopy, handleHistory],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableDataWithColumns = useMemo(
        () => ({
            columns,
            data: tableData.data,
        }),
        [columns, tableData.data],
    );

    const isFetchingData = isLoading || isRefetching || isFetching;

    if (!currentCompany) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>
                    No Partner / Company / Wholesaler Selected
                </AlertTitle>
                <Text>Please select a wholesaler and a partner / company.</Text>
            </Alert>
        );
    }

    const mainTable = (
        <Container
            as="div"
            maxW="container.xl"
            px={0}
            aria-label="triggers main table">
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                Triggers
            </Text>
            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isLoading={isFetchingData}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
                overlayNoRowsTemplate="No Triggers Found"
                titleName="Triggers"
                extraComponents={
                    <div style={{ paddingLeft: '20px' }}>
                        <Button
                            size="sm"
                            width="110px"
                            onClick={() => handleAdd()}
                            data-walkthroughid={addMode.ufAddBtn}>
                            + Add Trigger
                        </Button>
                    </div>
                }
            />
            <TriggerCopyModal
                triggerID={selectedTriggerId}
                setSelectedTriggerId={setSelectedTriggerId}
                resetToMainTable={resetToMainTable}
                isOpen={isCopyOpen}
                onClose={onCopyClose}
                darkMode={darkMode}
                switchMode={switchMode}
            />
        </Container>
    );
    const historyList = tableData?.data || [];
    const historyTable = (
        <Container
            as="div"
            maxW="container.xl"
            px={0}
            aria-label="triggers history table">
            <Breadcrumbs
                aria-label="breadcrumb"
                style={{ marginBottom: '20px' }}>
                <Link
                    color="inherit"
                    href="/"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                        e.preventDefault();
                        resetToMainTable();
                    }}>
                    All Triggers
                </Link>
                <div style={{ minWidth: 500 }}>
                    <ChakraSelect
                        value={
                            Array.isArray(historyList) && historyList.length > 0
                                ? historyList.find(
                                      (trigger) =>
                                          trigger.id === selectedTriggerId,
                                  )
                                : null
                        }
                        options={Array.isArray(historyList) ? historyList : []}
                        placeholder="Select a trigger"
                        isLoading={isLoading}
                        isSearchable={true}
                        menuPortalTarget={document.body}
                        chakraStyles={{
                            ...defaultChakraSelectStyle,
                            ...chakraSelectDark,
                        }}
                        styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        getOptionLabel={(trigger) => {
                            if (!trigger) return '';
                            return trigger.name;
                        }}
                        getOptionValue={(trigger) => trigger?.id || ''}
                        onChange={(selectedTrigger) => {
                            setSelectedTriggerId(selectedTrigger?.id || null);
                        }}
                    />
                </div>
            </Breadcrumbs>
            <History
                triggerId={selectedTriggerId}
                resetToMainTable={resetToMainTable}
            />
        </Container>
    );

    // Function to determine content based on mode
    const modalContent = () => {
        switch (mode) {
            case 'MainTable':
                return mainTable;
            case 'HistoryTable':
                return historyTable;
            case 'Add':
                return (
                    <TriggersEdit
                        mode={mode}
                        resetToMainTable={resetToMainTable}
                    />
                );
            case 'Edit':
                return (
                    <TriggersEdit
                        mode={mode}
                        resetToMainTable={resetToMainTable}
                        selectedTriggerID={selectedTriggerId}
                    />
                );
            case 'Loading':
                return <LoadingIndicator />;
            default:
                return mainTable;
        }
    };

    // Return the content based on current mode
    return modalContent();
};

export default Triggers;
